/**
 * Bundle Yotpo Rating Widget
 * Displays aggregated star rating and review count for bundle products
 *
 * Uses the same API approach as bundle-yotpo-aggregated-reviews
 */

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.bundleYotpoRatingInitialized) {
    return;
  }
  window.bundleYotpoRatingInitialized = true;

  const YOTPO_APP_KEY = '7MWKpDOGOknXBXpo12TeaGWtIAs67lY9tGHxmig5';

  // Cache for API responses to avoid duplicate requests
  const reviewCache = new Map();

  /**
   * Fetch reviews for a single product from Yotpo API
   * @param {number} productId - Shopify product ID
   * @returns {Promise<{averageScore: number, reviewCount: number} | null>}
   */
  async function fetchProductReviews(productId) {
    // Check cache first
    if (reviewCache.has(productId)) {
      return reviewCache.get(productId);
    }

    try {
      const response = await fetch(
        `https://api.yotpo.com/v1/widget/${YOTPO_APP_KEY}/products/${productId}/reviews.json`
      );

      if (!response.ok) {
        reviewCache.set(productId, null);
        return null;
      }

      const json = await response.json();
      const bottomline = json?.response?.bottomline;

      if (!bottomline) {
        reviewCache.set(productId, null);
        return null;
      }

      const result = {
        averageScore: bottomline.average_score,
        reviewCount: bottomline.total_review
      };

      // Cache the result
      reviewCache.set(productId, result);

      return result;
    } catch (error) {
      console.error('[Bundle Yotpo Rating] Error fetching reviews for product ' + productId + ':', error);
      reviewCache.set(productId, null);
      return null;
    }
  }

  /**
   * Aggregate reviews from multiple products using weighted average
   * @param {number[]} productIds - Array of Shopify product IDs
   * @returns {Promise<{averageRating: number, totalReviews: number} | null>}
   */
  async function fetchAggregatedReviews(productIds) {
    try {
      // Fetch all reviews in parallel for performance (same as bundle-yotpo-aggregated-reviews)
      const reviewsPromises = productIds.map(id => fetchProductReviews(id));
      const reviewsResults = await Promise.all(reviewsPromises);

      // Filter out null results (products with no reviews)
      const validReviews = reviewsResults.filter(review => review !== null);

      if (validReviews.length === 0) {
        return null;
      }

      // Calculate weighted average: Σ(rating × count) / Σ(count)
      let totalWeightedScore = 0;
      let totalReviewCount = 0;

      for (const review of validReviews) {
        totalWeightedScore += review.averageScore * review.reviewCount;
        totalReviewCount += review.reviewCount;
      }

      if (totalReviewCount === 0) {
        return null;
      }

      const averageRating = totalWeightedScore / totalReviewCount;

      return {
        averageRating: averageRating,
        totalReviews: totalReviewCount
      };
    } catch (error) {
      console.error('[Bundle Yotpo Rating] Error aggregating reviews:', error);
      return null;
    }
  }

  /**
   * Create star rating HTML
   * @param {number} rating - Rating score (0-5)
   * @returns {string} HTML string for stars
   */
  function createStarRating(rating) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let html = '';

    // Full stars
    for (let i = 0; i < fullStars; i++) {
      html += '<span class="bundle-yotpo-rating__star bundle-yotpo-rating__star--full">★</span>';
    }

    // Half star
    if (hasHalfStar) {
      html += '<span class="bundle-yotpo-rating__star bundle-yotpo-rating__star--half">★</span>';
    }

    // Empty stars
    for (let i = 0; i < emptyStars; i++) {
      html += '<span class="bundle-yotpo-rating__star bundle-yotpo-rating__star--empty">★</span>';
    }

    return html;
  }

  /**
   * Initialize a single rating widget
   * @param {HTMLElement} element - The widget container element
   */
  async function initRatingWidget(element) {
    // Skip if already initialized
    if (element.dataset.initialized === 'true') {
      return;
    }
    element.dataset.initialized = 'true';

    const dataElement = element.querySelector('.js-bundle-rating-data');
    const loadingElement = element.querySelector('.js-rating-loading');
    const contentElement = element.querySelector('.js-rating-content');
    const starsElement = element.querySelector('.js-rating-stars');
    const countElement = element.querySelector('.js-rating-count');

    if (!dataElement || !loadingElement || !contentElement) {
      return;
    }

    try {
      // Parse bundle data
      const bundleData = JSON.parse(dataElement.textContent || '{}');
      const childProducts = bundleData.childProducts || [];

      if (childProducts.length === 0) {
        // Hide widget if no child products
        element.style.display = 'none';
        return;
      }

      // Extract child product IDs
      const productIds = childProducts.map(child => child.id);

      // Fetch aggregated reviews
      const aggregatedData = await fetchAggregatedReviews(productIds);

      if (!aggregatedData || aggregatedData.totalReviews === 0) {
        // Hide widget if no reviews
        element.style.display = 'none';
        return;
      }

      // Render stars
      if (starsElement) {
        starsElement.innerHTML = createStarRating(aggregatedData.averageRating);
        starsElement.setAttribute('aria-label', `${aggregatedData.averageRating.toFixed(1)} out of 5 stars`);
      }

      // Render count
      if (countElement) {
        countElement.textContent = `[${aggregatedData.totalReviews}]`;
      }

      // Show content, hide loading
      loadingElement.style.display = 'none';
      contentElement.style.display = 'block';
    } catch (error) {
      console.error('[Bundle Yotpo Rating] Error initializing widget:', error);
      // Hide widget on error
      element.style.display = 'none';
    }
  }

  /**
   * Initialize all rating widgets on the page
   */
  async function initAllRatingWidgets() {
    const widgets = document.querySelectorAll('.js-bundle-yotpo-rating');

    if (widgets.length === 0) {
      return;
    }

    // Initialize all widgets in parallel
    await Promise.all(Array.from(widgets).map(widget => initRatingWidget(widget)));
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllRatingWidgets);
  } else {
    initAllRatingWidgets();
  }

  // Re-initialize on Shopify section load (for theme editor)
  if (typeof Shopify !== 'undefined') {
    document.addEventListener('shopify:section:load', initAllRatingWidgets);
  }

  // Expose function globally for dynamic content updates
  window.initBundleYotpoRating = initAllRatingWidgets;
})();
